import React, { useState } from 'react';
import {
  Loader2, Send, Linkedin, CheckCircle2, XCircle, Link2, Building2, PlugZap,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

function DiagList({ checks }) {
  return (
    <div className="space-y-1.5 mt-3" data-testid="diag-list">
      {checks.map((c) => (
        <div key={c.key} className="flex items-start gap-2 text-sm">
          {c.ok ? <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
            : <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />}
          <div>
            <span className={c.ok ? 'text-gray-700' : 'text-red-600 font-medium'}>{c.label}</span>
            {c.detail ? <span className="text-gray-400"> — {c.detail}</span> : null}
            {!c.ok && c.fix ? <p className="text-xs text-amber-700 mt-0.5">→ {c.fix}</p> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function Step({ n, children }) {
  return (
    <li className="flex gap-2 text-sm text-gray-600">
      <span className="w-5 h-5 rounded-full bg-habita/10 text-habita text-xs font-bold flex items-center justify-center shrink-0">{n}</span>
      <span>{children}</span>
    </li>
  );
}

export default function TelegramLinkedInSetup({ settings, onReload }) {
  const { toast } = useToast();
  const [tgTesting, setTgTesting] = useState(false);
  const [tgDiag, setTgDiag] = useState(null);
  const [liTesting, setLiTesting] = useState(false);
  const [liDiag, setLiDiag] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [orgs, setOrgs] = useState(null);
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  const li = settings?.linkedin || {};
  const tg = settings?.telegram || {};

  const testTelegram = async () => {
    setTgTesting(true);
    try { setTgDiag(await api.telegramTest()); }
    catch (e) { toast({ title: 'Error', description: String(e.message || e), variant: 'destructive' }); }
    finally { setTgTesting(false); }
  };

  const testLinkedin = async () => {
    setLiTesting(true);
    try { setLiDiag(await api.linkedinTest()); }
    catch (e) { toast({ title: 'Error', description: String(e.message || e), variant: 'destructive' }); }
    finally { setLiTesting(false); }
  };

  const connectLinkedin = async () => {
    setConnecting(true);
    try {
      const { url } = await api.linkedinAuthorizeUrl();
      window.open(url, 'li-oauth', 'width=600,height=720');
      toast({ title: 'Autoriza en la ventana de LinkedIn', description: 'Al terminar, pulsa "Cargar páginas".' });
    } catch (e) {
      toast({ title: 'No se pudo iniciar OAuth', description: String(e.message || e), variant: 'destructive' });
    } finally { setConnecting(false); }
  };

  const disconnectLinkedin = async () => {
    if (!window.confirm('¿Desconectar LinkedIn? Se borra el token y la página (Client ID/Secret se conservan). No afecta Meta/Telegram/TikTok.')) return;
    try {
      await api.linkedinDisconnect();
      setOrgs(null);
      setLiDiag(null);
      onReload?.();
      toast({ title: 'LinkedIn desconectado' });
    } catch (e) { toast({ title: 'Error al desconectar', description: String(e.message || e), variant: 'destructive' }); }
  };

  const loadOrgs = async () => {
    setLoadingOrgs(true);
    try { setOrgs((await api.linkedinOrganizations()).organizations || []); }
    catch (e) { toast({ title: 'Error al cargar páginas', description: String(e.message || e), variant: 'destructive' }); }
    finally { setLoadingOrgs(false); }
  };

  const selectOrg = async (o) => {
    try {
      await api.linkedinSelectOrg({ org_urn: o.org_urn, org_name: o.name });
      toast({ title: 'Página seleccionada', description: o.name });
      onReload?.();
    } catch (e) { toast({ title: 'Error', description: String(e.message || e), variant: 'destructive' }); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Telegram */}
      <div className="bg-white rounded-2xl p-5 border border-gray-100" data-testid="telegram-setup">
        <div className="flex items-center gap-2 mb-3">
          <Send className="w-5 h-5 text-cyan-600" />
          <h3 className="font-bold text-habita-dark">Telegram — Bot API oficial</h3>
          {tg.telegram_live_ready && <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-bold">Listo en vivo</span>}
        </div>
        <ol className="space-y-1.5 mb-4">
          <Step n={1}>Crea un <strong>canal</strong> en Telegram (público o privado).</Step>
          <Step n={2}>Abre <strong>@BotFather</strong> → <code className="bg-gray-100 px-1 rounded">/newbot</code> → copia el <strong>Bot Token</strong> y pégalo arriba.</Step>
          <Step n={3}>Agrega el bot como <strong>administrador del canal</strong> con permiso para publicar.</Step>
          <Step n={4}>Usa el <strong>@usuario del canal</strong> (ej. <code className="bg-gray-100 px-1 rounded">@habita</code>) o su ID <code className="bg-gray-100 px-1 rounded">-100…</code> como <strong>Chat ID</strong>.</Step>
        </ol>
        <button onClick={testTelegram} disabled={tgTesting} data-testid="telegram-test-btn"
          className="px-4 h-9 rounded-xl bg-cyan-600 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
          {tgTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlugZap className="w-4 h-4" />} Probar conexión
        </button>
        {tgDiag && <DiagList checks={tgDiag.checks} />}
      </div>

      {/* LinkedIn */}
      <div className="bg-white rounded-2xl p-5 border border-gray-100" data-testid="linkedin-setup">
        <div className="flex items-center gap-2 mb-3">
          <Linkedin className="w-5 h-5 text-sky-700" />
          <h3 className="font-bold text-habita-dark">LinkedIn — OAuth página de empresa</h3>
          {li.connected
            ? <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-bold" data-testid="linkedin-status-badge">Conectado</span>
            : li.connection_status === 'reconnect_required'
              ? <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold" data-testid="linkedin-status-badge">Reconexión requerida</span>
              : <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-bold" data-testid="linkedin-status-badge">No conectado</span>}
        </div>
        {li.connection_status === 'reconnect_required' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-2 mb-3 text-xs text-red-700" data-testid="linkedin-reconnect-banner">
            El token de LinkedIn dejó de ser válido. Pulsa <b>Conectar LinkedIn</b> para reautorizar.
          </div>
        )}
        <ol className="space-y-1.5 mb-4">
          <Step n={1}>Crea una app en <strong>LinkedIn Developers</strong> y solicita el producto <strong>Community Management API</strong>.</Step>
          <Step n={2}>Registra este <strong>Redirect URI</strong>: <code className="bg-gray-100 px-1 rounded break-all">{(process.env.REACT_APP_BACKEND_URL || '') + '/api/admin/distribution/linkedin/callback'}</code></Step>
          <Step n={3}>Copia el <strong>Client ID</strong> y <strong>Client Secret</strong> y pégalos arriba, luego guarda.</Step>
          <Step n={4}>Pulsa <strong>Conectar LinkedIn</strong>, autoriza y <strong>selecciona tu página de empresa</strong>.</Step>
        </ol>
        <div className="flex flex-wrap gap-2">
          <button onClick={connectLinkedin} disabled={connecting || !li.oauth_ready} data-testid="linkedin-connect-btn"
            title={li.oauth_ready ? '' : 'Guarda primero el Client ID y Secret'}
            className="px-4 h-9 rounded-xl bg-sky-700 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
            {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Conectar LinkedIn
          </button>
          {li.connected && (
            <button onClick={loadOrgs} disabled={loadingOrgs} data-testid="linkedin-load-orgs-btn"
              className="px-4 h-9 rounded-xl border border-gray-200 text-sm font-semibold flex items-center gap-2">
              {loadingOrgs ? <Loader2 className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4" />} Cargar páginas
            </button>
          )}
          <button onClick={testLinkedin} disabled={liTesting} data-testid="linkedin-test-btn"
            className="px-4 h-9 rounded-xl border border-gray-200 text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
            {liTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlugZap className="w-4 h-4" />} Probar conexión
          </button>
          {li.connected && (
            <button onClick={disconnectLinkedin} data-testid="linkedin-disconnect-btn"
              className="px-4 h-9 rounded-xl border border-rose-200 text-rose-600 text-sm font-semibold flex items-center gap-2 hover:bg-rose-50">
              Desconectar
            </button>
          )}
        </div>
        {li.org_name && <p className="text-xs text-gray-500 mt-2">Página activa: <strong>{li.org_name}</strong></p>}
        {orgs && (
          <div className="mt-3 space-y-1.5" data-testid="linkedin-orgs">
            {orgs.length === 0 ? <p className="text-sm text-gray-400">No se encontraron páginas administradas.</p>
              : orgs.map((o) => (
                <button key={o.org_urn} onClick={() => selectOrg(o)}
                  className="w-full text-left px-3 py-2 rounded-lg border border-gray-200 hover:border-habita text-sm flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-sky-700" /> {o.name}
                </button>
              ))}
          </div>
        )}
        {liDiag && <DiagList checks={liDiag.checks} />}
      </div>
    </div>
  );
}
